
import { Component, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import Pagination from "@/components/common/Pagination.vue";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import IconDownload from "@/components/common/icon/IconDownload.vue";
import ImportsFilter from "@/components/reports/ImportsFilter.vue";
import VueFilters from "@/vue-filters";
import VueMethods from "@/vue-methods";
import { BvTableFieldArray } from "bootstrap-vue";
import {
  EDirection,
  IImportRecord,
  IImportsFilterParams,
  ImportStatusEnum,
  ImportTypeEnum,
  IPageResponse,
} from "@/services";
import { AxiosResponse } from "axios";
import { SweetAlertResult } from "sweetalert2";
import { i18n } from "@/i18n";

@Component({
  name: "imports-table",
  mixins: [VueFilters, VueMethods],
  components: { ImportsFilter, IconTrash, IconDownload, Pagination },
})
export default class ImportsTable extends Vue {
  @Action("fetchImportsPage") fetchImportsPage!: (
    params: IImportsFilterParams
  ) => Promise<IPageResponse<IImportRecord> | undefined>;
  @Action("fetchDeleteImport") fetchDeleteImport!: (id: number) => Promise<boolean>;
  @Action("fetchDownloadImport") fetchDownloadImport!: (
    id: number
  ) => Promise<AxiosResponse<Blob> | undefined>;

  imports: IPageResponse<IImportRecord> | undefined = {
    content: [] as IImportRecord[],
  } as IPageResponse<IImportRecord>;
  private importParams: IImportsFilterParams = {
    countries: undefined,
    userTypes: undefined,
    usernameOrIdPrecise: false,
    usernameOrId: "",
    importTypes: undefined,
    requestedAt: undefined,
    statuses: undefined,
  };

  private pageNumber = 1;
  private pageSize = 10;
  private totalElements = 0;
  private orderBy = "requestedAt";
  private sortDesc = true;

  mounted(): void {
    this.fetchImports();
  }

  async filterImports(): Promise<void> {
    this.pageNumber = 1;
    await this.fetchImports();
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.fetchImports();
  }

  async fetchImports(): Promise<void> {
    const params: IImportsFilterParams = {
      ...this.importParams,
      page: this.pageNumber - 1,
      size: this.pageSize,
      orderBy: this.orderBy || undefined,
      direction: this.sortDesc ? EDirection.DESC : EDirection.ASC,
    };
    this.imports = await this.fetchImportsPage(params);
    this.totalElements = this.imports?.totalElements || 0;
  }

  async downloadReport(id: number): Promise<void> {
    const res = await this.fetchDownloadImport(id);
    if (res) {
      this.downloadFile(res);
    }
  }

  async removeReport(id: number): Promise<void> {
    const { isConfirmed } = await this.modalConfirmRemove();
    if (isConfirmed) {
      const ok = await this.fetchDeleteImport(id);
      if (ok) {
        await this.fetchImports();
      }
    }
  }

  async modalConfirmRemove(): Promise<SweetAlertResult> {
    return this.$swal({
      title: i18n.t("message.generic.confirm.title").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  resolveReportType(importType: string | undefined): string {
    if (importType === ImportTypeEnum.SERVICE_POINT) {
      return this.$t("label.import.type.servicepoint") as string;
    }
    return importType || "";
  }

  resolveStatus(status: ImportStatusEnum | undefined): string {
    switch (status) {
      case ImportStatusEnum.PENDING:
        return this.$t("label.import.status.pending") as string;
      case ImportStatusEnum.IN_PROGRESS:
        return this.$t("label.import.status.in_progress") as string;
      case ImportStatusEnum.UPLOADED:
        return this.$t("label.import.status.uploaded") as string;
      default:
        return status || "";
    }
  }

  isUploaded(status?: ImportStatusEnum): boolean {
    return status === ImportStatusEnum.UPLOADED;
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
      },
      {
        key: "country.name",
        label: this.$t("label.country") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter(country: string): string {
          if (!country) return "";
          return country.charAt(0).toUpperCase() + country.slice(1).toLowerCase();
        },
      },
      {
        key: "requestedBy.username",
        label: this.$t("label.user") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "type",
        label: this.$t("column.import.type") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (_value: string, _key: string, item: IImportRecord): string =>
          this.resolveReportType(item.type),
      },
      {
        key: "originalFileName",
        label: this.$t("column.filename") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "status",
        label: this.$t("column.status") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (_value: string, _key: string, item: IImportRecord): string =>
          this.resolveStatus(item.status),
      },
      {
        key: "requestedAt",
        label: this.$t("column.request_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "createdAt",
        label: this.$t("column.creation_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "expirationAt",
        label: this.$t("column.expiration_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "progressPercentage",
        label: this.$t("column.import.progress") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
    ];
  }
}
